import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Key, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('email'); // 'email', 'code', 'newPassword', 'success'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    setStep('email');
    setEmail('');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    onClose();
  };

  const sendResetCode = async () => {
    if (!email) {
      setError('Veuillez entrer votre email');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      toast.success('Code de réinitialisation envoyé !');
      setStep('code');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de l\'envoi';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const verifyCodeAndReset = async () => {
    if (code.length !== 6) {
      setError('Le code doit contenir 6 chiffres');
      return;
    }
    if (newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        email,
        code,
        new_password: newPassword
      });
      toast.success('Mot de passe réinitialisé avec succès !');
      setStep('success');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Code invalide ou expiré';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const renderEmailStep = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Key className="w-8 h-8 text-amber-600" />
        </div>
        <p className="text-sm text-slate-500">
          Entrez votre email pour recevoir un code de réinitialisation
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="vous@exemple.com"
          autoFocus
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <Button variant="outline" onClick={handleClose} className="flex-1">
          Annuler
        </Button>
        <Button onClick={sendResetCode} disabled={loading} className="flex-1">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
          Envoyer le code
        </Button>
      </div>
    </div>
  );

  const renderCodeStep = () => (
    <div className="space-y-4">
      <button
        onClick={() => setStep('email')}
        className="flex items-center text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Retour
      </button>

      <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm text-blue-800">
        <p>Un code a été envoyé à <strong>{email}</strong></p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="code">Code de vérification</Label>
        <Input
          id="code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          maxLength={6}
          className="text-center text-2xl tracking-widest font-mono"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="newPassword">Nouveau mot de passe</Label>
        <Input
          id="newPassword"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Minimum 6 caractères"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
        <Input
          id="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirmez votre mot de passe"
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <Button onClick={verifyCodeAndReset} disabled={loading} className="w-full">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Réinitialiser le mot de passe
      </Button>

      <button
        onClick={sendResetCode}
        className="text-sm text-slate-500 hover:text-slate-700 underline w-full text-center"
        disabled={loading}
      >
        Renvoyer le code
      </button>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="text-center py-6">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <CheckCircle className="w-8 h-8 text-green-600" />
      </div>
      <h3 className="text-xl font-semibold text-slate-900 mb-2">Mot de passe réinitialisé !</h3>
      <p className="text-slate-500 mb-6">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
      <Button onClick={handleClose} className="w-full">
        Retour à la connexion
      </Button>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="forgot-password-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-900">
            {step === 'success' ? 'Succès' : 'Mot de passe oublié'}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          {step === 'email' && renderEmailStep()}
          {step === 'code' && renderCodeStep()}
          {step === 'success' && renderSuccessStep()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;
